/* Speed Army - Rim Strip Editor
 * Live-recolours the wheel mockups extracted from the Photoshop data sets
 * (scripts/sa-rim-extract.py). One wheel photo + three alpha masks per model:
 *   ud = top/bottom strips (Main colour), lr = left/right strips (Secondary colour),
 *   in = inner-rim model lettering (its RGB carries the rim shading, applied as
 *        a 69% luminosity blend, exactly like the PSD's "wheel shade" layer).
 * No dependencies. Progressive: the form posts plain line-item properties even
 * if this script never runs.
 */
(function () {
  'use strict';

  // PSD "wheel shade" layer: luminosity blend, opacity 69 on Photoshop's 0-255 scale (= 27%),
  // which matches the composite (white lettering comes out ~194 grey, not ~100).
  var SHADE_ALPHA = 69 / 255;

  function q(root, sel) { return root.querySelector(sel); }
  function qa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function norm(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ''); }
  function esc(s) { return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  /* Design <-> URL: compact JSON, base64url. Kept short so it fits comfortably in an order line. */
  function encodeDesign(d) {
    return btoa(unescape(encodeURIComponent(JSON.stringify(d)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function readDesignParam() {
    try {
      var p = new URLSearchParams(window.location.search).get('d');
      if (!p) return null;
      var b = p.replace(/-/g, '+').replace(/_/g, '/');
      while (b.length % 4) b += '=';
      return JSON.parse(decodeURIComponent(escape(atob(b))));
    } catch (e) { return null; }
  }
  /* Shopify money_format ("£{{amount}}") for a price in minor units. */
  function money(cents, fmt) {
    var n = cents / 100, s = n.toFixed(2);
    return String(fmt || '{{amount}}')
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(n)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, s.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(n)))
      .replace(/\{\{\s*amount\s*\}\}/g, s)
      .replace(/<[^>]+>/g, '');
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () { resolve(im); };
      im.onerror = function () { reject(new Error('Could not load ' + src)); };
      im.src = src;
    });
  }

  function Editor(root) {
    this.root = root;
    var cfg = JSON.parse(q(root, '[data-sa-rim-config]').textContent);
    this.cfg = cfg;
    // Images live either next to the index (theme assets) or in Shopify Files (cfg.assetBase).
    this.base = cfg.assetBase || cfg.modelsUrl.replace(/[^\/]*$/, '');
    // theme assets: reuse the index's ?v= cache-buster so re-uploads are not served stale
    var v = cfg.assetBase ? null : cfg.modelsUrl.match(/\?v=[^&]+/);
    this.ver = v ? v[0] : '';
    this.cache = {};
    this.state = { model: null, main: null, secondary: null, inside: null, insideOn: cfg.insideDefault !== false, wheel: 'black' };
    this.restore = readDesignParam();   // ?d=<design> re-opens a design (the "Preview link" saved with each order)
    this.canvas = q(root, 'canvas');
    this.ctx = this.canvas.getContext('2d');
    this.status = q(root, '[data-sa-status]');
    var self = this;
    fetch(cfg.modelsUrl).then(function (r) { return r.json(); }).then(function (index) {
      self.index = index;
      self.applyPaletteOverride();
      self.buildControls();
      self.pickInitialModel();
      if (self.restore) self.applyRestore(self.restore);
      self.setModel(self.state.model, true);
    }).catch(function (e) { self.fail(e); });
  }

  Editor.prototype.fail = function (e) {
    this.root.setAttribute('data-sa-ready', 'error');
    if (this.status) this.status.textContent = 'Preview unavailable: ' + e.message;
  };

  Editor.prototype.applyPaletteOverride = function () {
    var extra = this.cfg.palette || '';
    if (!extra.trim()) return;
    var list = [];
    extra.split(/\r?\n/).forEach(function (line) {
      var m = line.match(/^\s*([^:#]+?)\s*:\s*(#[0-9a-f]{3,8})\s*$/i);
      if (m) list.push({ id: norm(m[1]), label: m[1].trim(), hex: m[2] });
    });
    if (list.length) this.index.palette = list;   // override replaces the palette entirely
  };

  Editor.prototype.models = function () {
    var out = [];
    this.index.brands.forEach(function (b) { b.models.forEach(function (m) { out.push({ id: m.id, label: m.label, brand: b.label, boxes: m.boxes, kits: m.kits || null }); }); });
    return out;
  };

  Editor.prototype.findModel = function (id) {
    return this.models().filter(function (m) { return m.id === id; })[0] || null;
  };

  Editor.prototype.brand = function (id) {
    return this.index.brands.filter(function (b) { return b.id === id; })[0] || null;
  };

  Editor.prototype.brandOf = function (modelId) {
    return this.index.brands.filter(function (b) { return b.models.some(function (m) { return m.id === modelId; }); })[0] || null;
  };

  /* The model dropdown only lists the chosen brand; it stays hidden until a brand is picked. */
  Editor.prototype.fillModels = function (brand) {
    var sel = q(this.root, '[data-sa-model]');
    if (!sel) return;
    sel.hidden = !brand;
    if (!brand) { sel.innerHTML = ''; sel.removeAttribute('data-sa-brand-id'); return; }
    if (sel.getAttribute('data-sa-brand-id') === brand.id) return;
    sel.setAttribute('data-sa-brand-id', brand.id);
    sel.innerHTML = brand.models.map(function (m) {
      return '<option value="' + esc(m.id) + '">' + esc(m.label) + '</option>';
    }).join('');
  };

  /* Priority: section setting > product tag rim:<key> > title/vendor guess > first model. */
  Editor.prototype.pickInitialModel = function () {
    var cfg = this.cfg, all = this.models();
    var wanted = cfg.modelKey || '';
    if (!wanted && cfg.tags) {
      cfg.tags.forEach(function (t) { var m = t.match(/^rim:(.+)$/i); if (m) wanted = m[1].trim(); });
    }
    if (wanted && this.findModel(wanted)) { this.state.model = wanted; return; }
    var hay = norm((cfg.vendor || '') + ' ' + (cfg.title || ''));
    var best = null;
    all.forEach(function (m) {
      var lab = norm(m.label), brand = norm(m.brand);
      if (lab.length < 2 || hay.indexOf(lab) < 0) return;
      var score = lab.length + (hay.indexOf(brand) >= 0 ? 100 : 0);
      if (!best || score > best.score) best = { id: m.id, score: score };
    });
    this.state.model = best ? best.id : all[0].id;
  };

  Editor.prototype.buildControls = function () {
    var self = this, idx = this.index, pal = idx.palette;
    var brandSel = q(this.root, '[data-sa-brand]'), sel = q(this.root, '[data-sa-model]');
    if (brandSel) {
      brandSel.innerHTML = '<option value="">Choose your brand...</option>' + idx.brands.map(function (b) {
        return '<option value="' + esc(b.id) + '">' + esc(b.label) + '</option>';
      }).join('');
      brandSel.addEventListener('change', function () {
        var b = self.brand(brandSel.value);
        self.fillModels(b);
        if (b && b.models.length) self.setModel(b.models[0].id);
      });
    }
    if (sel) sel.addEventListener('change', function () { if (sel.value) self.setModel(sel.value); });
    qa(this.root, '[data-sa-swatches]').forEach(function (box) {
      var zone = box.getAttribute('data-sa-swatches');
      box.innerHTML = pal.map(function (c) {
        var light = luminance(c.hex) > 0.8;
        return '<button type="button" class="sa-rim__swatch' + (light ? ' sa-rim__swatch--light' : '') + '" role="radio" aria-checked="false" ' +
          'data-zone="' + zone + '" data-id="' + esc(c.id) + '" data-hex="' + esc(c.hex) + '" title="' + esc(c.label) + '" aria-label="' + esc(c.label) + '" ' +
          'style="--sa-swatch:' + esc(c.hex) + '"></button>';
      }).join('');
      box.addEventListener('click', function (ev) {
        var b = ev.target.closest('.sa-rim__swatch');
        if (b) self.setColour(zone, b.getAttribute('data-id'));
      });
    });
    var wheels = q(this.root, '[data-sa-wheels]');
    if (wheels) {
      // Common wheel finishes first (section setting); the rest sit behind "More colours".
      var first = (this.cfg.wheelColours || []).map(function (s) { return String(s).trim().toLowerCase(); }).filter(Boolean);
      var list = idx.wheels.slice();
      if (first.length) {
        list.sort(function (a, b) {
          var ia = first.indexOf(a.id), ib = first.indexOf(b.id);
          return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        });
      }
      wheels.innerHTML = list.map(function (w) {
        var extra = first.length && first.indexOf(w.id) < 0;
        return '<button type="button" class="sa-rim__wheel" role="radio" aria-checked="false" data-id="' + esc(w.id) + '" aria-label="' + esc(w.label) + ' wheel" title="' + esc(w.label) + '"' + (extra ? ' data-extra hidden' : '') + '>' +
          '<img src="' + esc(self.base + w.src + self.ver) + '" alt="" loading="lazy" width="48" height="48"><span>' + esc(w.label) + '</span></button>';
      }).join('');
      var more = q(this.root, '[data-sa-wheels-more]'), extras = qa(wheels, '[data-extra]');
      if (more && extras.length) {
        more.hidden = false;
        more.textContent = '+' + extras.length + ' more';
        more.addEventListener('click', function () { extras.forEach(function (b) { b.hidden = false; }); more.hidden = true; });
      }
      wheels.addEventListener('click', function (ev) {
        var b = ev.target.closest('.sa-rim__wheel');
        if (b) { self.state.wheel = b.getAttribute('data-id'); self.sync(); self.render(); }
      });
    }
    var toggle = q(this.root, '[data-sa-inside-toggle]');
    if (toggle) {
      toggle.checked = this.state.insideOn;
      toggle.addEventListener('change', function () { self.state.insideOn = toggle.checked; self.sync(); self.render(); });
    }
    var dl = q(this.root, '[data-sa-download]');
    if (dl) dl.addEventListener('click', function () { self.download(); });
    var form = q(this.root, 'form');
    if (form && this.cfg.ajaxCart) form.addEventListener('submit', function (ev) { self.submit(ev, form); });
    this.initVariants();
    // wheel sizes post straight from their own selects (form="..."); the engine only mirrors them
    qa(this.root, '[data-sa-size]').forEach(function (sel) { sel.addEventListener('change', function () { self.sync(); }); });
    if (this.initCustom) this.initCustom();   // sa-rim-custom.js, when the section enables "Create your own"

    // defaults: white main, red secondary, inside follows main
    var ids = pal.map(function (c) { return c.id; });
    this.state.main = ids.indexOf('white') >= 0 ? 'white' : ids[0];
    this.state.secondary = ids.indexOf('red') >= 0 ? 'red' : ids[Math.min(1, ids.length - 1)];
    this.state.inside = this.state.main;
  };

  function luminance(hex) {
    var h = hex.replace('#', '');
    if (h.length === 3) h = h.replace(/(.)/g, '$1$1');
    var r = parseInt(h.substr(0, 2), 16) / 255, g = parseInt(h.substr(2, 2), 16) / 255, b = parseInt(h.substr(4, 2), 16) / 255;
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  Editor.prototype.colour = function (id) {
    return this.index.palette.filter(function (c) { return c.id === id; })[0] || this.index.palette[0];
  };

  Editor.prototype.setColour = function (zone, id) {
    this.state[zone] = id;
    this.sync();
    this.render();
  };

  Editor.prototype.setModel = function (id, initial) {
    var self = this, m = this.findModel(id);
    if (!m) return;
    this.state.model = id;
    var brandSel = q(this.root, '[data-sa-brand]'), sel = q(this.root, '[data-sa-model]'), b = this.brandOf(id);
    if (brandSel && b && brandSel.value !== b.id) brandSel.value = b.id;
    this.fillModels(b);
    if (sel && sel.value !== id) sel.value = id;
    this.root.setAttribute('data-sa-ready', 'loading');
    if (this.status) this.status.textContent = 'Loading ' + m.brand + ' ' + m.label + ' preview...';
    this.load(m).then(function () {
      self.root.setAttribute('data-sa-ready', 'true');
      if (self.status) self.status.textContent = '';
      self.sync();
      self.render();
    }).catch(function (e) { self.fail(e); });
  };

  Editor.prototype.load = function (m) {
    var self = this, key = m.id;
    if (this.cache[key]) return Promise.resolve(this.cache[key]);
    return Promise.all([
      loadImage(this.base + 'sa-rim-' + key + '-ud.png' + this.ver),
      loadImage(this.base + 'sa-rim-' + key + '-lr.png' + this.ver),
      loadImage(this.base + 'sa-rim-' + key + '-in.png' + this.ver)
    ]).then(function (imgs) {
      self.cache[key] = { ud: imgs[0], lr: imgs[1], inside: imgs[2] };
      return self.cache[key];
    });
  };

  Editor.prototype.wheelImage = function () {
    var self = this, w = this.wheelEntry();
    var key = 'wheel:' + w.id;
    if (this.cache[key]) return Promise.resolve(this.cache[key]);
    return loadImage(this.base + w.src + this.ver).then(function (im) { self.cache[key] = im; return im; });
  };

  /* Paint: wheel photo, then each strip mask filled with its colour. */
  Editor.prototype.render = function () {
    var self = this, layers = this.cache[this.state.model];
    if (!layers && this.state.mode !== 'custom') return;
    var token = this.renderToken = (this.renderToken || 0) + 1;
    this.wheelImage().then(function (wheel) {
      if (token !== self.renderToken) return;
      var c = self.canvas, ctx = self.ctx, W = self.index.frame[0], H = self.index.frame[1];
      if (c.width !== W || c.height !== H) { c.width = W; c.height = H; }
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.drawImage(wheel, 0, 0, W, H);
      // Per-wheel "fit": an affine that moves the strip masks onto this photo's rim, so wheel
      // photos with a slightly different rim ellipse work without re-cutting 1,100 masks.
      var fit = self.wheelEntry().fit;
      if (fit && fit.length === 6) ctx.setTransform(fit[0], fit[1], fit[2], fit[3], fit[4], fit[5]);
      if (self.state.mode === 'custom' && self.renderCustom) { self.renderCustom(ctx, W, H); ctx.setTransform(1, 0, 0, 1, 0, 0); return; }
      ctx.drawImage(self.tint(layers.ud, self.colour(self.state.main).hex, false), 0, 0);
      ctx.drawImage(self.tint(layers.lr, self.colour(self.state.secondary).hex, false), 0, 0);
      if (self.state.insideOn) ctx.drawImage(self.tint(layers.inside, self.colour(self.state.inside).hex, true), 0, 0);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    });
  };

  Editor.prototype.wheelEntry = function () {
    var id = this.state.wheel;
    return this.index.wheels.filter(function (x) { return x.id === id; })[0] || this.index.wheels[0];
  };

  Editor.prototype.tint = function (mask, hex, shaded) {
    var W = this.index.frame[0], H = this.index.frame[1];
    var off = this.off || (this.off = document.createElement('canvas'));
    if (off.width !== W || off.height !== H) { off.width = W; off.height = H; }
    var o = off.getContext('2d');
    o.globalCompositeOperation = 'source-over';
    o.globalAlpha = 1;
    o.clearRect(0, 0, W, H);
    o.drawImage(mask, 0, 0, W, H);
    o.globalCompositeOperation = 'source-in';
    o.fillStyle = hex;
    o.fillRect(0, 0, W, H);
    if (shaded) {                       // PSD "wheel shade": luminosity blend at 69%
      o.globalCompositeOperation = 'luminosity';
      o.globalAlpha = SHADE_ALPHA;
      o.drawImage(mask, 0, 0, W, H);
      o.globalAlpha = 1;
      o.globalCompositeOperation = 'source-over';
    }
    return off;
  };

  /* Reflect state into swatch aria, summary text and the form's line-item properties. */
  Editor.prototype.sync = function () {
    var s = this.state, self = this, m = this.findModel(s.model);
    qa(this.root, '.sa-rim__swatch').forEach(function (b) {
      b.setAttribute('aria-checked', String(s[b.getAttribute('data-zone')] === b.getAttribute('data-id')));
    });
    qa(this.root, '.sa-rim__wheel').forEach(function (b) { b.setAttribute('aria-checked', String(s.wheel === b.getAttribute('data-id'))); });
    ['main', 'secondary', 'inside'].forEach(function (zone) {
      var lab = q(self.root, '[data-sa-label="' + zone + '"]');
      if (lab) lab.textContent = self.colour(s[zone]).label;
    });
    var insideRow = q(this.root, '[data-sa-inside-row]');
    if (insideRow) insideRow.hidden = !s.insideOn;
    var custom = s.mode === 'custom';
    var props = {
      'Bike model': custom ? 'Custom text: ' + (s.text || '') : (m ? m.brand + ' ' + m.label : ''),
      'Main colour (top & bottom strips)': this.colour(s.main).label,
      'Secondary colour (side strips)': this.colour(s.secondary).label,
      'Inner rim logo': s.insideOn ? this.colour(s.inside).label : 'None',
      'Preview link': this.designLink()
    };
    Object.keys(props).forEach(function (k) {
      var inp = q(self.root, 'input[name="properties[' + k + ']"]');
      if (inp) inp.value = props[k];
    });
    var sizes = qa(this.root, '[data-sa-size]'), sizeText = '';
    if (sizes.length) {
      var box = q(this.root, '[data-sa-sizes]'), std = (box.getAttribute('data-standard') || '') + '"';
      var nonStd = sizes.some(function (sel) { return sel.value !== std; });
      var note = q(this.root, '[data-sa-size-note]');
      if (note) note.hidden = !nonStd;
      sizeText = ' - wheels ' + sizes.map(function (sel) { return sel.value; }).join(' / ');
    }
    var sum = q(this.root, '[data-sa-summary]');
    if (sum) sum.textContent = (custom ? '"' + (s.text || '') + '" in ' + s.font : props['Bike model']) + ' - ' + props['Main colour (top & bottom strips)'] + ' / ' + props['Secondary colour (side strips)'] + (s.insideOn ? ' / logo ' + props['Inner rim logo'] : ', no inner logo') + sizeText;
    this.resolveProduct();
    if (this.current) this.updateVariant();   // the Kit option (logo / stripes only) follows the design
  };

  /* The whole design as a URL: the current page with ?d=<design>. Saved as the "Preview link"
     line-item property so the order can be re-opened exactly as the customer saw it. */
  Editor.prototype.designLink = function () {
    var s = this.state;
    var d = { m: s.model, a: s.main, b: s.secondary, i: s.inside, on: s.insideOn ? 1 : 0, w: s.wheel };
    if (s.mode === 'custom') { d.mode = 'custom'; d.t = s.text || ''; d.f = s.font || ''; }
    var sizes = qa(this.root, '[data-sa-size]').map(function (sel) { return sel.value; });
    if (sizes.length) d.sz = sizes;
    var u;
    try { u = new URL(this.cfg.designerUrl || window.location.href, window.location.href); } catch (e) { u = new URL(window.location.href); }
    u.searchParams.delete('d');
    u.searchParams.set('d', encodeDesign(d));
    return u.toString();
  };

  Editor.prototype.applyRestore = function (d) {
    var self = this, s = this.state, ids = this.index.palette.map(function (c) { return c.id; });
    if (d.m && this.findModel(d.m)) s.model = d.m;
    ['a', 'b', 'i'].forEach(function (k, n) { if (ids.indexOf(d[k]) >= 0) s[['main', 'secondary', 'inside'][n]] = d[k]; });
    if (d.on === 0 || d.on === 1) s.insideOn = !!d.on;
    var toggle = q(this.root, '[data-sa-inside-toggle]'); if (toggle) toggle.checked = s.insideOn;
    if (d.w && this.index.wheels.some(function (w) { return w.id === d.w; })) s.wheel = d.w;
    if (d.sz) qa(this.root, '[data-sa-size]').forEach(function (sel, n) { if (d.sz[n]) sel.value = d.sz[n]; });
    if (d.mode === 'custom' && this.setMode) {
      s.text = String(d.t || '').toUpperCase().slice(0, 16);
      if (d.f) s.font = d.f;
      var t = q(this.root, '[data-sa-text]'), f = q(this.root, '[data-sa-font]');
      if (t) t.value = s.text;
      if (f) f.value = s.font;
      this.setMode('custom');
    }
  };

  /* Which product the design is sold as:
       Create your own            -> "product for Create your own" (or the fallback)
       model, inner logo on       -> the model's Full Wheel Rim Stripe kit (or the fallback)
       model, inner logo off      -> the model's Wheel Rim Stripe kit (or the stripes-only fallback)
     Prices and variant ids come live from /products/<handle>.js. */
  Editor.prototype.wantedHandle = function () {
    var cfg = this.cfg, s = this.state, m = this.findModel(s.model);
    if (s.mode === 'custom') return cfg.customHandle || cfg.fallbackHandle;
    var kits = (cfg.useKits !== false && m && m.kits) || {};
    if (s.insideOn) return kits.full || cfg.fallbackHandle;
    return kits.stripe || cfg.plainHandle || cfg.fallbackHandle;
  };

  Editor.prototype.productUrl = function (handle) {
    var base = this.cfg.productJsonBase || '/';
    if (base.charAt(base.length - 1) !== '/') base += '/';
    return base + 'products/' + encodeURIComponent(handle) + '.js';
  };

  Editor.prototype.resolveProduct = function () {
    var self = this, handle = this.wantedHandle();
    if (!handle || !this.fallback) return;
    if (handle === this.productHandle) return;
    this.productHandle = handle;
    if (handle === this.cfg.fallbackHandle) { this.applyProduct(this.fallback); return; }
    this.products = this.products || {};
    if (!this.products[handle]) {
      this.products[handle] = fetch(this.productUrl(handle), { headers: { 'Accept': 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
        .then(function (p) {
          return {
            handle: p.handle, title: p.title,
            optionNames: (p.options || []).map(function (o) { return o.name; }),
            variants: (p.variants || []).map(function (v) {
              return { id: v.id, price: money(v.price, self.cfg.moneyFormat), available: v.available, options: v.options || [v.option1, v.option2, v.option3] };
            })
          };
        })
        .catch(function () { return null; });
    }
    var btn = q(this.root, '[data-sa-add]');
    if (btn) btn.setAttribute('aria-busy', 'true');
    this.products[handle].then(function (prod) {
      if (self.productHandle !== handle) return;      // the design changed again meanwhile
      if (btn) btn.removeAttribute('aria-busy');
      if (!prod) { self.productHandle = self.cfg.fallbackHandle; self.applyProduct(self.fallback); return; }
      self.applyProduct(prod);
    });
  };

  Editor.prototype.applyProduct = function (prod) {
    this.current = prod;
    var kit = q(this.root, '[data-sa-kit]');
    if (kit) kit.textContent = prod.title ? 'Kit: ' + prod.title : '';
    this.updateVariant();
  };

  /* Product options: the visible selects must match; the option hidden by the
     section (e.g. "Style") is free, so the first available variant wins. */
  Editor.prototype.initVariants = function () {
    var self = this, node = q(this.root, '[data-sa-variants]');
    if (!node) return;
    try { this.variants = JSON.parse(node.textContent); } catch (e) { this.variants = []; }
    this.fallback = { handle: this.cfg.fallbackHandle, title: this.cfg.fallbackTitle || '', optionNames: this.cfg.fallbackOptions || [], variants: this.variants };
    this.productHandle = this.cfg.fallbackHandle;
    qa(this.root, '[data-sa-option]').forEach(function (sel) { sel.addEventListener('change', function () { self.updateVariant(); }); });
    this.applyProduct(this.fallback);
  };

  Editor.prototype.updateVariant = function () {
    var prod = this.current || this.fallback || { optionNames: [], variants: this.variants || [] };
    var picks = {};
    qa(this.root, '[data-sa-option]').forEach(function (sel) {
      var name = sel.getAttribute('data-sa-option-name'), idx = prod.optionNames.indexOf(name);
      if (idx < 0 && prod.optionNames.length) idx = -1; else if (idx < 0) idx = parseInt(sel.getAttribute('data-sa-option'), 10);
      var wrap = sel.closest('label') || sel;
      wrap.hidden = idx < 0;                          // e.g. no "Finish" on the plain stripe kits
      if (idx >= 0) picks[idx] = sel.value;
    });
    // Product option driven by the design itself: "Kit" = with inner rim logo / stripes only.
    var cfg = this.cfg, kitIdx = cfg.kitOption ? prod.optionNames.indexOf(cfg.kitOption) : -1;
    if (kitIdx >= 0) picks[kitIdx] = (this.state.mode === 'custom' || this.state.insideOn) ? cfg.kitLogo : cfg.kitPlain;
    var matches = (prod.variants || []).filter(function (v) {
      return Object.keys(picks).every(function (i) { return v.options[i] === picks[i]; });
    });
    var chosen = matches.filter(function (v) { return v.available; })[0] || matches[0];
    var idInput = q(this.root, '[data-sa-variant]'), price = q(this.root, '[data-sa-price]'), btn = q(this.root, '[data-sa-add]');
    if (!chosen) {
      if (btn) { btn.disabled = true; btn.textContent = 'Unavailable'; }
      return;
    }
    if (idInput) idInput.value = chosen.id;
    if (price) price.textContent = chosen.price;
    if (btn) { btn.disabled = !chosen.available; btn.textContent = chosen.available ? btn.getAttribute('data-label') : 'Sold out'; }
  };

  Editor.prototype.download = function () {
    var m = this.findModel(this.state.model), a = document.createElement('a');
    var tag = this.state.mode === 'custom' ? 'custom-' + (this.state.text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-') : (m ? m.id : 'rim');
    a.download = 'speedarmy-' + tag + '-' + this.state.main + '-' + this.state.secondary + '.png';
    a.href = this.canvas.toDataURL('image/png');
    document.body.appendChild(a); a.click(); a.remove();
  };

  /* Dawn/Ride-style AJAX add: refresh the cart drawer if the theme has one, else go to the cart. */
  Editor.prototype.submit = function (ev, form) {
    ev.preventDefault();
    var self = this, btn = q(form, '[type="submit"]'), fd = new FormData(form);
    var drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (drawer) fd.append('sections', (drawer.getSectionsToRender ? drawer.getSectionsToRender().map(function (s) { return s.id; }) : ['cart-drawer', 'cart-icon-bubble']).join(','));
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
    fetch(this.cfg.cartAddUrl || '/cart/add.js', { method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' } })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status && res.status >= 400) throw new Error(res.description || res.message || 'Could not add to cart');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(res);
          if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
        } else {
          window.location.href = self.cfg.cartUrl || '/cart';
        }
      })
      .catch(function (e) { if (self.status) self.status.textContent = e.message; })
      .then(function () { if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); } });
  };

  function init() {
    qa(document, '[data-sa-rim]').forEach(function (root) {
      if (!root.saRim) root.saRim = new Editor(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
  window.SARimEditor = Editor;
})();
